import { useEffect, useState } from "react";
import { Download } from "lucide-react";
import { api, fmtL, download, errMsg } from "@/lib/api";
import { Spinner, EmptyState, TeamBadge } from "@/components/opl/Common";
import { toast } from "sonner";

const Kpi = ({ l, v, testid, dark }) => (
  <div className={`rounded-2xl p-5 ${dark ? "bg-[#0F172A] text-white" : "bg-white border border-[#E7E5E0]"}`}>
    <p className={`text-xs uppercase tracking-widest ${dark ? "text-amber-300" : "text-slate-500"}`}>{l}</p>
    <p className={`num text-3xl font-bold mt-1 ${dark ? "shine" : "text-[#0F172A]"}`} data-testid={testid}>{v}</p>
  </div>
);

export default function Results() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { api.get("/public/results").then((r) => setD(r.data)).catch((e) => setErr(errMsg(e))); }, []);
  if (err) return <div className="max-w-7xl mx-auto p-10"><EmptyState title={err} /></div>;
  if (!d) return <Spinner />;
  const dl = (fmt) => download(`/public/results?fmt=${fmt}`, `opl_results.${fmt}`).catch((e) => toast.error(errMsg(e)));
  return (
    <div className="max-w-7xl mx-auto px-5 lg:px-8 py-14">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div><p className="eyebrow">Ledger</p><h1 className="text-4xl sm:text-5xl font-bold mt-2 text-[#0F172A]">Auction results</h1></div>
        {d.allow_download && (
          <div className="flex gap-2">
            {["csv", "xlsx", "pdf"].map((f) => <button key={f} onClick={() => dl(f)} data-testid={`results-download-${f}`} className="flex items-center gap-1 px-4 py-2 rounded-full border border-[#E7E5E0] bg-white text-sm font-medium text-[#0F172A] hover:border-amber-400 transition-colors"><Download className="h-4 w-4" />{f.toUpperCase()}</button>)}
          </div>
        )}
      </div>
      <div className="grid grid-cols-3 gap-3 mt-8">
        <Kpi l="Sold" v={d.sold.length} testid="results-sold-count" />
        <Kpi l="Unsold" v={d.unsold.length} />
        <Kpi l="Total spend" v={fmtL(d.total_spent)} testid="results-total-spend" dark />
      </div>
      <h2 className="text-2xl font-bold mt-14 mb-4 text-[#0F172A]">Highest-value purchases</h2>
      {d.sold.length ? (
        <div className="bg-white border border-[#E7E5E0] rounded-2xl overflow-x-auto" data-testid="results-sold-table">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[11px] uppercase tracking-widest text-slate-500 bg-[#F7F6F2]"><th className="p-3">#</th><th className="p-3">Player</th><th className="p-3">Role</th><th className="p-3">Team</th><th className="p-3 text-right">Points</th></tr></thead>
            <tbody>{d.sold.map((p, i) => <tr key={p.id} className="border-t border-[#F0EEE8] hover:bg-amber-50/50 transition-colors"><td className="p-3 num text-slate-400">{i + 1}</td><td className="p-3 font-semibold text-[#0F172A]">{p.name}</td><td className="p-3 text-slate-500">{p.role}</td><td className="p-3 text-slate-700">{p.team_name}</td><td className="p-3 text-right num font-bold text-amber-700">{fmtL(p.sold_price)}</td></tr>)}</tbody>
          </table>
        </div>
      ) : <EmptyState title="No players sold yet" />}
      <h2 className="text-2xl font-bold mt-14 mb-4 text-[#0F172A]">Team-wise acquisitions</h2>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {d.by_team.map((t) => (
          <div key={t.id} className="card-l p-5 hover:!transform-none">
            <div className="flex items-center gap-3"><TeamBadge team={t} /><div className="flex-1 font-semibold truncate text-[#0F172A]">{t.name}</div>{t.spent != null && <span className="num font-semibold text-amber-700">{fmtL(t.spent)}</span>}</div>
            <div className="mt-3 space-y-1 text-sm">{t.players.map((p) => <div key={p.id} className="flex justify-between"><span className="text-slate-700">{p.name}</span><span className="num text-slate-500">{fmtL(p.sold_price)}</span></div>)}{!t.players.length && <p className="text-slate-400">—</p>}</div>
          </div>
        ))}
      </div>
      <h2 className="text-2xl font-bold mt-14 mb-4 text-[#0F172A]">Unsold players</h2>
      <div className="flex flex-wrap gap-2">{d.unsold.map((p) => <span key={p.id} className="bg-white border border-[#E7E5E0] rounded-full px-3 py-1 text-sm text-slate-700">{p.name}</span>)}{!d.unsold.length && <p className="text-slate-400">None</p>}</div>
    </div>
  );
}
